import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { updateUserDetails } from "../services/update-user-details";
import { ConsentFormStatus } from "@/pages/users/types/user";

export function useUpdateUserDetails() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({
      user_id,
      name,
      email,
      phone_number,
      country,
      age,
      language,
      sex,
      document_ids,
      hrc_status,
      mc_status,
      risk_category,
      is_test_user,
      allow_onboarding_flow,
      region_id,
    }: {
      user_id: string;
      name?: string | null;
      email?: string | null;
      phone_number?: string | null;
      country?: string | null;
      age?: number | null;
      language?: string[] | null;
      sex?: string | null;
      document_ids?: Record<string, string> | null;
      hrc_status?: ConsentFormStatus | undefined;
      mc_status? : ConsentFormStatus | undefined;
      risk_category?: string | null;
      is_test_user?: boolean;
      allow_onboarding_flow?: boolean;
      region_id?: string | null;
    }) =>
      updateUserDetails({
        user_id,
        name,
        email,
        phone_number,
        country,
        age,
        language,
        sex,
        document_ids,
        hrc_status,
        mc_status,
        risk_category,
        is_test_user,
        allow_onboarding_flow,
        region_id,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["user"], exact: false });
      queryClient.invalidateQueries({ queryKey: ["users"], exact: false });
      // The couples-pack cards embed the partner's name, and their query has a
      // 30-minute staleTime — so without this an edit made in the drawer leaves
      // the card behind it showing the old name, which reads as a failed save.
      // Matches use-update-couples-pack-users / -professional, which already
      // invalidate this key after their own mutations.
      queryClient.invalidateQueries({
        queryKey: ["couples-packs"],
        exact: false,
      });
      toast({
        title: "Success",
        description: "User profile details updated successfully",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error?.message || "Failed to update user profile details",
        variant: "destructive",
      });
    },
  });
}

